import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import styles from './FeedbackTriagePanel.module.css';

/**
 * FeedbackTriagePanel — admin-only modal for triaging /wrong: and /feedback:
 * reports captured from Foreman conversations.
 *
 * Approving a report POSTs to /api/auth/admin/feedback/:id/approve, which
 * (server-side) creates a `knowledge_entries` row of kind='correction' AND
 * regenerates the workspace `recent_corrections.md` file Foreman reads on
 * each turn. PATCH /:id is used to mark triaged / resolved / wont_fix without
 * spawning a KB entry.
 */

interface FeedbackTriagePanelProps {
  open: boolean;
  onClose: () => void;
}

type FeedbackStatus = 'open' | 'triaged' | 'resolved' | 'wont_fix' | 'approved';
type FeedbackCategory = 'wrong' | 'missing' | 'too_wordy' | 'other' | string;

interface FeedbackReport {
  id: number;
  session_key: string | null;
  message_id: string | null;
  reporter: string;
  category: FeedbackCategory;
  reason: string;
  suggested_fix: string | null;
  status: FeedbackStatus;
  triaged_by: string | null;
  triaged_at: number | null;
  resolution_note: string | null;
  spawned_kb_id: number | null;
  created_at: number;
}

interface FeedbackListResp {
  reports: FeedbackReport[];
}

interface ToastMsg {
  text: string;
  kind: 'ok' | 'err';
}

const TOKEN_KEY = 'foreman-auth-token';

function authHeader(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY) || '';
  return { Authorization: `Bearer ${token}` };
}

async function readJsonOrThrow(res: Response): Promise<unknown> {
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data as { error?: string }).error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function relativeTime(unixSeconds: number): string {
  if (!unixSeconds) return '—';
  const now = Date.now() / 1000;
  const diff = now - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString();
}

function shortKey(s: string | null, n = 10): string {
  if (!s) return '—';
  if (s.length <= n) return s;
  return `${s.slice(0, n)}…`;
}

function categoryLabel(cat: FeedbackCategory): string {
  switch (cat) {
    case 'wrong': return 'Wrong';
    case 'missing': return 'Missing';
    case 'too_wordy': return 'Too wordy';
    case 'other': return 'Other';
    default: return cat;
  }
}

function statusLabel(s: FeedbackStatus): string {
  switch (s) {
    case 'open': return 'open';
    case 'triaged': return 'triaged';
    case 'resolved': return 'resolved';
    case 'wont_fix': return "won't fix";
    case 'approved': return 'approved';
    default: return s;
  }
}

function statusPillClass(s: FeedbackStatus): string {
  switch (s) {
    case 'open': return styles.statusOpen;
    case 'triaged': return styles.statusTriaged;
    case 'resolved': return styles.statusResolved;
    case 'wont_fix': return styles.statusWontFix;
    case 'approved': return styles.statusApproved;
    default: return styles.statusOpen;
  }
}

/** Default body shape mirrors the server-side synthesizer in task #19 §4 so
 *  the admin sees roughly what the KB entry will look like before saving. */
function defaultEditedBody(r: FeedbackReport): string {
  const lines: string[] = [];
  lines.push(`**Category:** ${categoryLabel(r.category)}`);
  lines.push(`**Reported by:** @${r.reporter}`);
  if (r.session_key) lines.push(`**Source thread:** \`${r.session_key}\``);
  if (r.message_id) lines.push(`**Source message:** \`${r.message_id}\``);
  lines.push('');
  lines.push('### What was wrong');
  lines.push(r.reason || '_(no reason given)_');
  if (r.suggested_fix) {
    lines.push('');
    lines.push('### How to fix it');
    lines.push(r.suggested_fix);
  }
  return lines.join('\n');
}

function defaultEditedTitle(r: FeedbackReport): string {
  const snippet = (r.reason || '').replace(/\s+/g, ' ').trim().slice(0, 60);
  return `Correction: ${snippet || categoryLabel(r.category)}`;
}

// ── Tabs ──────────────────────────────────────────────────────────────────

type TabKey = 'open' | 'triaged' | 'resolved' | 'wont_fix' | 'approved' | 'all';

const TABS: { key: TabKey; label: string; status: FeedbackStatus | null }[] = [
  { key: 'open',     label: 'Open',      status: 'open' },
  { key: 'triaged',  label: 'Triaged',   status: 'triaged' },
  { key: 'resolved', label: 'Resolved',  status: 'resolved' },
  { key: 'wont_fix', label: "Won't fix", status: 'wont_fix' },
  { key: 'approved', label: 'Approved',  status: 'approved' },
  { key: 'all',      label: 'All',       status: null },
];

// ── Edit-form state ──────────────────────────────────────────────────────

interface EditState {
  title: string;
  body: string;
  confidence: number;
  tags: string[];
  tagDraft: string;
}

function initialEdit(r: FeedbackReport): EditState {
  return {
    title: defaultEditedTitle(r),
    body: defaultEditedBody(r),
    confidence: 0.85,
    tags: ['user-feedback', String(r.category)],
    tagDraft: '',
  };
}

// ── Component ────────────────────────────────────────────────────────────

export function FeedbackTriagePanel({ open, onClose }: FeedbackTriagePanelProps) {
  const [tab, setTab] = useState<TabKey>('open');
  const [reports, setReports] = useState<FeedbackReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMsg | null>(null);

  // Row-level state
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<Record<number, string>>({});
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // Edit-form state, keyed by report id (only one open at a time but we
  // store as a map so reordering after refresh doesn't lose the draft).
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);

  const showToast = useCallback((text: string, kind: 'ok' | 'err' = 'ok') => {
    setToast({ text, kind });
    window.setTimeout(() => setToast(null), 2800);
  }, []);

  // ── Fetching ────────────────────────────────────────────────────────────

  const currentStatus: FeedbackStatus | null = useMemo(
    () => TABS.find((t) => t.key === tab)?.status ?? null,
    [tab]
  );

  const fetchReports = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (currentStatus) params.set('status', currentStatus);
        params.set('limit', '50');
        const res = await fetch(`/api/auth/admin/feedback?${params.toString()}`, {
          headers: authHeader(),
        });
        const data = (await readJsonOrThrow(res)) as FeedbackListResp;
        setReports(Array.isArray(data.reports) ? data.reports : []);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to load feedback';
        setError(msg);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [currentStatus]
  );

  useEffect(() => {
    if (!open) return;
    fetchReports(false);
  }, [open, fetchReports]);

  // Esc to close (don't trap if an edit form is open — let editor swallow it).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editingId !== null) {
          setEditingId(null);
          setEditState(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, editingId]);

  // ── Actions ─────────────────────────────────────────────────────────────

  /** Approve with optional edits. Empty body → server uses its synthesized
   *  defaults. We pass the form values straight through. */
  const handleApprove = useCallback(
    async (id: number, overrides?: {
      editedTitle?: string;
      editedBody?: string;
      confidence?: number;
      tags?: string[];
    }) => {
      setPendingId(id);
      setRowError((prev) => {
        const { [id]: _drop, ...rest } = prev;
        void _drop;
        return rest;
      });
      try {
        const body: Record<string, unknown> = {};
        if (overrides?.editedTitle !== undefined) body.editedTitle = overrides.editedTitle;
        if (overrides?.editedBody !== undefined) body.editedBody = overrides.editedBody;
        if (overrides?.confidence !== undefined) body.confidence = overrides.confidence;
        if (overrides?.tags !== undefined) body.tags = overrides.tags;
        const res = await fetch(`/api/auth/admin/feedback/${id}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify(body),
        });
        const data = (await readJsonOrThrow(res)) as {
          ok?: boolean;
          feedbackReport?: FeedbackReport;
          knowledgeEntry?: { id?: number };
        };
        showToast(
          `Approved → KB entry ${data.knowledgeEntry?.id != null ? `#${data.knowledgeEntry.id}` : 'created'}`,
          'ok'
        );
        setEditingId((cur) => (cur === id ? null : cur));
        setEditState((cur) => (editingId === id ? null : cur));
        await fetchReports(true);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Approve failed';
        // Race-condition hint: another admin may have already approved.
        // Server typically returns 409/410 with a helpful message.
        setRowError((prev) => ({ ...prev, [id]: msg }));
        // Re-fetch in case the row is stale — the user will see the new
        // status on the next render.
        fetchReports(true).catch(() => {});
      } finally {
        setPendingId((cur) => (cur === id ? null : cur));
      }
    },
    [editingId, fetchReports, showToast]
  );

  const handlePatchStatus = useCallback(
    async (
      id: number,
      nextStatus: 'triaged' | 'resolved' | 'wont_fix',
      askForNote: boolean
    ) => {
      let resolutionNote: string | undefined;
      if (askForNote) {
        // eslint-disable-next-line no-alert
        const note = window.prompt(
          nextStatus === 'wont_fix'
            ? `Optional note: why won't we fix this?`
            : `Optional resolution note:`,
          ''
        );
        // null = cancel
        if (note === null) return;
        const trimmed = note.trim();
        if (trimmed.length > 0) resolutionNote = trimmed;
      }

      setPendingId(id);
      setRowError((prev) => {
        const { [id]: _drop, ...rest } = prev;
        void _drop;
        return rest;
      });
      try {
        const res = await fetch(`/api/auth/admin/feedback/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ status: nextStatus, resolutionNote }),
        });
        await readJsonOrThrow(res);
        showToast(`Marked ${statusLabel(nextStatus)}`, 'ok');
        await fetchReports(true);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Update failed';
        setRowError((prev) => ({ ...prev, [id]: msg }));
        fetchReports(true).catch(() => {});
      } finally {
        setPendingId((cur) => (cur === id ? null : cur));
      }
    },
    [fetchReports, showToast]
  );

  // ── Edit form helpers ───────────────────────────────────────────────────

  const openEditor = useCallback((r: FeedbackReport) => {
    setEditingId(r.id);
    setEditState(initialEdit(r));
  }, []);

  const closeEditor = useCallback(() => {
    setEditingId(null);
    setEditState(null);
  }, []);

  const handleAddTag = useCallback(() => {
    setEditState((prev) => {
      if (!prev) return prev;
      const t = prev.tagDraft.trim().toLowerCase();
      if (!t) return prev;
      if (prev.tags.includes(t)) return { ...prev, tagDraft: '' };
      return { ...prev, tags: [...prev.tags, t], tagDraft: '' };
    });
  }, []);

  const handleTagKey = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        handleAddTag();
      } else if (e.key === 'Backspace' && editState && editState.tagDraft === '') {
        setEditState((prev) => (prev ? { ...prev, tags: prev.tags.slice(0, -1) } : prev));
      }
    },
    [editState, handleAddTag]
  );

  const handleSubmitEdit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (editingId === null || !editState) return;
      handleApprove(editingId, {
        editedTitle: editState.title,
        editedBody: editState.body,
        confidence: editState.confidence,
        tags: editState.tags,
      });
    },
    [editingId, editState, handleApprove]
  );

  // ── Source-chip handlers ────────────────────────────────────────────────

  const handleCopySource = useCallback(
    async (label: string, value: string | null) => {
      if (!value) return;
      const ok = await copyToClipboard(value);
      showToast(ok ? `${label} copied` : `${label}: ${value}`, ok ? 'ok' : 'err');
    },
    [showToast]
  );

  // ── Counters used in tab labels ─────────────────────────────────────────
  // We only have an accurate count for the *current* tab. The Open count is
  // surfaced separately via the TopBar badge polling logic — here we just
  // show the count of the currently-loaded list.
  const tabCount = useMemo(() => (loading ? null : reports.length), [loading, reports.length]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Foreman feedback queue"
      >
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <h2 className={styles.title}>Foreman feedback queue</h2>
            <div className={styles.subtitle}>
              {tabCount === null
                ? 'Loading…'
                : `${tabCount} ${statusLabel((currentStatus || 'all') as FeedbackStatus)} report${tabCount === 1 ? '' : 's'}`}
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            {'✕'}
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.tabs}>
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {tab === t.key && tabCount !== null && (
                  <span className={styles.tabBadge}>{tabCount}</span>
                )}
              </button>
            ))}
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={() => fetchReports(false)}
              disabled={loading}
              title="Refresh"
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {error && <div className={styles.err}>{error}</div>}

          {loading ? (
            <div className={styles.loading}>
              <span className={styles.spinner} aria-hidden /> Loading feedback…
            </div>
          ) : reports.length === 0 ? (
            <div className={styles.empty}>
              No feedback in this bucket.
              <span className={styles.emptyHint}>
                Foreman is currently doing fine, or it's making mistakes nobody is flagging.
              </span>
            </div>
          ) : (
            <div className={styles.list}>
              {reports.map((r) => {
                const busy = pendingId === r.id;
                const isExpanded = expanded[r.id] === true;
                const reasonTrimmed = (r.reason || '').trim();
                const showExpand = reasonTrimmed.length > 200;
                const reasonText = showExpand && !isExpanded
                  ? `${reasonTrimmed.slice(0, 200)}…`
                  : reasonTrimmed || '_(no reason given)_';
                const editing = editingId === r.id;
                const status = r.status;
                const rErr = rowError[r.id];
                return (
                  <div key={r.id} className={styles.card}>
                    <div className={styles.cardHeader}>
                      <span className={`${styles.statusPill} ${statusPillClass(status)}`}>
                        {statusLabel(status)}
                      </span>
                      <span className={styles.categoryBadge}>{categoryLabel(r.category)}</span>
                      <span className={styles.reporter}>
                        @{r.reporter}
                        <span className={styles.reporterTime}>
                          {' · reported '}{relativeTime(r.created_at)}
                        </span>
                      </span>
                      {r.spawned_kb_id != null && (
                        <span className={styles.spawnedKb}>
                          {'→ KB entry #'}{r.spawned_kb_id}
                        </span>
                      )}
                    </div>

                    <div className={styles.reason}>
                      {reasonText}
                      {showExpand && (
                        <button
                          type="button"
                          className={styles.expandBtn}
                          onClick={() =>
                            setExpanded((prev) => ({ ...prev, [r.id]: !isExpanded }))
                          }
                        >
                          {isExpanded ? 'show less' : 'show more'}
                        </button>
                      )}
                    </div>

                    {r.suggested_fix && (
                      <div className={styles.suggested}>
                        Suggested fix: {r.suggested_fix}
                      </div>
                    )}

                    <div className={styles.sourceRow}>
                      <button
                        type="button"
                        className={styles.sourceChip}
                        onClick={() => handleCopySource('Thread key', r.session_key)}
                        disabled={!r.session_key}
                        title={r.session_key || 'No thread key'}
                      >
                        Thread {shortKey(r.session_key, 14)}
                      </button>
                      <button
                        type="button"
                        className={styles.sourceChip}
                        onClick={() => handleCopySource('Message id', r.message_id)}
                        disabled={!r.message_id}
                        title={r.message_id || 'No message id'}
                      >
                        msg {shortKey(r.message_id, 10)}
                      </button>
                      {r.triaged_by && (
                        <span>
                          {'triaged by @'}{r.triaged_by}
                          {r.triaged_at ? ` · ${relativeTime(r.triaged_at)}` : ''}
                        </span>
                      )}
                      {r.resolution_note && <span>note: {r.resolution_note}</span>}
                    </div>

                    {rErr && <div className={styles.rowError}>{rErr}</div>}

                    {editing && editState ? (
                      <form className={styles.editForm} onSubmit={handleSubmitEdit}>
                        <div className={styles.field}>
                          <label className={styles.fieldLabel} htmlFor={`ftp-title-${r.id}`}>
                            Title
                          </label>
                          <input
                            id={`ftp-title-${r.id}`}
                            type="text"
                            required
                            className={styles.input}
                            value={editState.title}
                            onChange={(e) =>
                              setEditState((prev) => prev && { ...prev, title: e.target.value })
                            }
                          />
                        </div>
                        <div className={styles.field}>
                          <label className={styles.fieldLabel} htmlFor={`ftp-body-${r.id}`}>
                            Body (markdown)
                          </label>
                          <textarea
                            id={`ftp-body-${r.id}`}
                            required
                            className={styles.textarea}
                            value={editState.body}
                            onChange={(e) =>
                              setEditState((prev) => prev && { ...prev, body: e.target.value })
                            }
                          />
                        </div>
                        <div className={styles.editGrid}>
                          <div className={styles.field}>
                            <label className={styles.fieldLabel} htmlFor={`ftp-conf-${r.id}`}>
                              Confidence: {editState.confidence.toFixed(2)}
                            </label>
                            <div className={styles.sliderRow}>
                              <input
                                id={`ftp-conf-${r.id}`}
                                type="range"
                                min={0.5}
                                max={1.0}
                                step={0.05}
                                className={styles.slider}
                                value={editState.confidence}
                                onChange={(e) =>
                                  setEditState((prev) =>
                                    prev && { ...prev, confidence: Number(e.target.value) }
                                  )
                                }
                              />
                            </div>
                          </div>
                          <div className={styles.field}>
                            <span className={styles.fieldLabel}>Tags</span>
                            <div className={styles.tagChips}>
                              {editState.tags.map((t) => (
                                <span key={t} className={styles.tagChip}>
                                  {t}
                                  <button
                                    type="button"
                                    className={styles.tagRemove}
                                    onClick={() =>
                                      setEditState((prev) =>
                                        prev && {
                                          ...prev,
                                          tags: prev.tags.filter((x) => x !== t),
                                        }
                                      )
                                    }
                                    aria-label={`Remove tag ${t}`}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                              <input
                                type="text"
                                className={styles.tagInput}
                                placeholder="add tag…"
                                value={editState.tagDraft}
                                onChange={(e) =>
                                  setEditState((prev) =>
                                    prev && { ...prev, tagDraft: e.target.value }
                                  )
                                }
                                onKeyDown={handleTagKey}
                                onBlur={handleAddTag}
                              />
                            </div>
                          </div>
                        </div>
                        <div className={styles.editActions}>
                          <button
                            type="button"
                            className={styles.cancelBtn}
                            onClick={closeEditor}
                            disabled={busy}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className={`${styles.actionBtn} ${styles.primaryAction}`}
                            disabled={busy || !editState.title.trim() || !editState.body.trim()}
                          >
                            {busy && <span className={styles.miniSpinner} aria-hidden />}
                            Save & approve
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className={styles.actions}>
                        {status === 'open' && (
                          <>
                            <button
                              type="button"
                              className={`${styles.actionBtn} ${styles.primaryAction}`}
                              onClick={() => handleApprove(r.id)}
                              disabled={busy}
                              title="Approve as-is and spawn a KB entry"
                            >
                              {busy && <span className={styles.miniSpinner} aria-hidden />}
                              Approve
                            </button>
                            <button
                              type="button"
                              className={styles.actionBtn}
                              onClick={() => openEditor(r)}
                              disabled={busy}
                            >
                              Edit & Approve
                            </button>
                            <button
                              type="button"
                              className={`${styles.actionBtn} ${styles.dangerAction}`}
                              onClick={() => handlePatchStatus(r.id, 'wont_fix', true)}
                              disabled={busy}
                            >
                              Won't Fix
                            </button>
                          </>
                        )}
                        {(status === 'triaged') && (
                          <>
                            <button
                              type="button"
                              className={`${styles.actionBtn} ${styles.primaryAction}`}
                              onClick={() => handleApprove(r.id)}
                              disabled={busy}
                            >
                              {busy && <span className={styles.miniSpinner} aria-hidden />}
                              Approve
                            </button>
                            <button
                              type="button"
                              className={styles.actionBtn}
                              onClick={() => openEditor(r)}
                              disabled={busy}
                            >
                              Edit & Approve
                            </button>
                            <button
                              type="button"
                              className={styles.actionBtn}
                              onClick={() => handlePatchStatus(r.id, 'resolved', true)}
                              disabled={busy}
                            >
                              Mark resolved
                            </button>
                            <button
                              type="button"
                              className={`${styles.actionBtn} ${styles.dangerAction}`}
                              onClick={() => handlePatchStatus(r.id, 'wont_fix', true)}
                              disabled={busy}
                            >
                              Won't Fix
                            </button>
                          </>
                        )}
                        {(status === 'resolved' || status === 'wont_fix' || status === 'approved') && (
                          <>
                            <button
                              type="button"
                              className={styles.actionBtn}
                              onClick={() => handlePatchStatus(r.id, 'triaged', false)}
                              disabled={busy}
                              title="Reopen for further review"
                            >
                              Reopen (mark triaged)
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {toast && (
          <div className={`${styles.toast} ${toast.kind === 'err' ? styles.toastErr : ''}`}>
            {toast.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Standalone badge logic helper ─────────────────────────────────────────
// Exported so the TopBar can poll without instantiating the full panel.

export function useOpenFeedbackCount(enabled: boolean): number {
  const [count, setCount] = useState(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await fetch('/api/auth/admin/feedback?status=open&limit=50', {
          headers: authHeader(),
        });
        if (!res.ok) {
          // 403 (not admin) or 401 — silently zero out.
          if (!cancelled) setCount(0);
          return;
        }
        const data = (await res.json()) as FeedbackListResp;
        if (!cancelled) setCount(Array.isArray(data.reports) ? data.reports.length : 0);
      } catch {
        // network blip — keep last value
      }
    };

    tick();
    const id = window.setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled]);

  return count;
}
